import React, { useMemo, useCallback } from 'react';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';

import List from '@gogame/ui/List';
import {
  Container,
  BaseListItem,
  BaseListSubheader,
  ScrollContainer,
} from './components';
import { useRowRenderer } from './useRowRenderer';

// import VirtualizedListItemHolder from './VirtualizedListItemHolder';
export const dataCy = 'virtualized-list';

const EMPTY_ARRAY = [];

export const VirtualizedList = (props) => {
  const {
    data = EMPTY_ARRAY,
    rowHeight = 48,

    minHeight,
    grouped,
    isInitialExpanded = () => false,
    getOptionLabel,
    getOptionValue,
  } = props;

  const getInitialExpanded = useCallback(
    (_) => {
      return data.filter((row) => isInitialExpanded(row));
    },
    [data]
  );

  const [expandedRows, setExpandedRows] = React.useState(getInitialExpanded);

  React.useEffect(() => {
    setExpandedRows(getInitialExpanded());
  }, [getInitialExpanded]);

  const parentRef = React.useRef(null);

  const { rows, groupIndexes } = useMemo(() => {
    if (grouped) {
      const groupIndexes = [];

      return {
        rows: data.reduce((acc, row) => {
          groupIndexes.push(acc.length);
          return [
            ...acc,
            row,
            ...(expandedRows.includes(row) ? row.children : []),
          ];
        }, []),
        groupIndexes: groupIndexes,
      };
    } else {
      return {
        rows: data || EMPTY_ARRAY,
        groupIndexes: EMPTY_ARRAY,
      };
    }
  }, [grouped, data, expandedRows]);

  const isGroup = ({ index }) => groupIndexes.includes(index);

  const activeStickyIndexRef = React.useRef(0);

  const isActiveSticky = (index) => false; //activeStickyIndexRef.current === index

  const rowVirtualizer = useVirtualizer({
    count: rows?.length || 0,
    debug: true,
    estimateSize: () => rowHeight,
    getScrollElement: () => parentRef.current,
    rangeExtractor: React.useCallback(
      (range) => {
        activeStickyIndexRef.current =
          [...groupIndexes]
            .reverse()
            .find((index) => range.startIndex >= index) ?? 0;

        const next = new Set([
          activeStickyIndexRef.current,
          ...defaultRangeExtractor(range),
        ]);
        const r = [...next].sort((a, b) => a - b);
        console.log('rangeExtractor.r', r);
        return r;
      },
      [groupIndexes]
    ),
  });

  const { renderRow, renderSubheader } = useRowRenderer({
    ...props,
    isExpanded: (row) => expandedRows.includes(row),
    getGroupChildren: (row) => row.children,
    onGroupClick: (_, row, { expanded }) =>
      setExpandedRows((prev) => {
        return expanded
          ? prev.filter((expandedRow) => expandedRow !== row)
          : [...prev, row];
      }),
    getOptionLabel,
    getOptionValue,
  });

  return (
    <Container minHeight={minHeight}>
      <ScrollContainer ref={parentRef}>
        <List
          component={'div'}
          disablePadding={true}
          sx={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];

            const parentIndex =
              groupIndexes.findIndex((index) => index > virtualRow.index) - 1;

            const parent =
              parentIndex >= 0 ? rows[groupIndexes[parentIndex]] : null;

            const siblings = parent?.children || EMPTY_ARRAY;

            if (isGroup(virtualRow)) {
              return (
                <VirtualizedListItemPosition
                  position={virtualRow.start}
                  height={virtualRow.size}
                  key={virtualRow.index}
                >
                  {renderSubheader(row, parent, siblings)}
                </VirtualizedListItemPosition>
              );
            }

            return (
              <VirtualizedListItemPosition
                position={virtualRow.start}
                height={virtualRow.size}
                key={virtualRow.index}
              >
                {renderRow(row, parent, siblings)}
              </VirtualizedListItemPosition>
            );
          })}
        </List>
      </ScrollContainer>
    </Container>
  );
};

const VirtualizedListItemPosition = React.forwardRef(
  ({ children, holder, sticky = false, height, position }, ref) => {
    return (
      <div
        ref={ref}
        style={{
          // background: '#fff',
          zIndex: 1,
          height: `${height}px`,
          ...(sticky
            ? { position: 'sticky', top: 0 }
            : {
                position: 'absolute',
                transform: `translateY(${position}px)`,
                top: 0,
                left: 0,
                width: '100%',
              }),
        }}
      >
        {children}
      </div>
    );
  }
);

export default VirtualizedList;
